import {
  configureStore,
  type Middleware,
  type Reducer,
  type ThunkAction,
  type UnknownAction,
} from '@reduxjs/toolkit';

import { baseApi } from '../api/baseApi';
import { authReducer, sessionExpired, type AuthState } from '../auth/authSlice';
import { bootstrapSession, changePin, login, logout } from '../auth/authThunks';
import { clearSessionSnapshot, writeSessionSnapshot } from '../auth/sessionSnapshot';
import { clearPhotoCache } from '../photos/photoObjectUrl';

type ExtraReducers = Record<string, Reducer>;

/**
 * A session change must not leave the previous caller's data behind in the
 * RTK Query cache. Without this, `getMe`/`listUsers({...})`/etc. keep
 * answering from cache for every arg combination already fetched, so a
 * second user signing in on the same tab (a shared trailer tablet) renders
 * the first user's data until each query happens to refetch on its own —
 * defeating the server's 404-not-403 cross-contract isolation on the client.
 * `logout` and `sessionExpired` both end in the same `anonymous` state, so
 * both must clear the cache the same way.
 */
const resetApiCacheOnSessionEnd: Middleware = (store) => (next) => (action) => {
  const result = next(action);
  if (logout.fulfilled.match(action) || logout.rejected.match(action) || sessionExpired.match(action)) {
    store.dispatch(baseApi.util.resetApiState());
  }
  return result;
};

/**
 * Keep the offline identity snapshot in step with the session.
 *
 * It is written from the state *after* the action rather than from each
 * thunk's own payload so there is one rule instead of four: whatever the slice
 * now says the session is, that is what a cold start with no signal will
 * rehydrate. `changePin` is what makes that worth doing — it clears
 * `mustChangePin` without carrying a `me` payload of its own, and a snapshot
 * left saying `true` would send a worker who has already chosen a PIN to a PIN
 * screen they cannot complete without a network.
 *
 * `status === 'authenticated'` is the gate, so the offline rehydration does
 * not immediately rewrite the record it was just read from. Everything is a
 * no-op unless the app named a database (`ApiClientRuntime.sessionSnapshotDb`);
 * `apps/admin-web` does not.
 */
const mirrorSessionSnapshot: Middleware = (store) => (next) => (action) => {
  const result = next(action);
  if (
    login.fulfilled.match(action) ||
    changePin.fulfilled.match(action) ||
    bootstrapSession.fulfilled.match(action)
  ) {
    const { auth } = store.getState() as { auth: AuthState };
    if (auth.status === 'authenticated' && auth.user !== null) {
      void writeSessionSnapshot({
        // A19: `email` and `phone` never reach IndexedDB. They are not
        // credentials and they are cleared on logout, but a shared plant phone
        // is a shared device, Law 25 (R-04) is still open, and rule 14 says
        // this database holds the device uuid and the locale. The known cost
        // is accepted: an offline Profil screen shows « aucun » for both until
        // the session verifies again.
        user: { ...auth.user, email: null, phone: null },
        locale: auth.user.locale,
        memberships: auth.memberships,
        permissions: auth.permissions,
        mustChangePin: auth.mustChangePin,
      });
    }
  }
  if (
    logout.fulfilled.match(action) ||
    logout.rejected.match(action) ||
    sessionExpired.match(action)
  ) {
    void clearSessionSnapshot();
    // Sessions do not share blobs (photoObjectUrl.ts) — a shared trailer
    // tablet must not keep the previous worker's face photo resolvable.
    clearPhotoCache();
  }
  return result;
};

export interface CreateAppStoreOptions<R extends ExtraReducers> {
  /** Per-app client-state slices (filters, calendar view, punch-queue mirror). */
  readonly reducers?: R;
  /** Extra middleware appended after the RTK Query middleware. */
  readonly middleware?: Middleware[];
}

/**
 * Both apps build their store here so the base slice, the auth slice and the
 * RTK Query middleware can never be wired up two different ways.
 *
 * No credential is persisted: the access token is in memory by design (D-05)
 * and the punch queue's system of record is IndexedDB, not Redux (D-17). The
 * one thing that outlives the tab is the offline identity snapshot above —
 * `/auth/me`'s payload, which is not a credential — and only for an app that
 * asked for it.
 */
export function createAppStore<R extends ExtraReducers = Record<string, never>>(
  options: CreateAppStoreOptions<R> = {},
) {
  return configureStore({
    reducer: {
      [baseApi.reducerPath]: baseApi.reducer,
      auth: authReducer,
      ...(options.reducers ?? ({} as R)),
    },
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().concat(
        baseApi.middleware,
        resetApiCacheOnSessionEnd,
        mirrorSessionSnapshot,
        ...(options.middleware ?? []),
      ),
  });
}

export type AppStoreOf<R extends ExtraReducers = Record<string, never>> = ReturnType<
  typeof createAppStore<R>
>;
export type RootStateOf<R extends ExtraReducers = Record<string, never>> = ReturnType<
  AppStoreOf<R>['getState']
>;
export type AppDispatchOf<R extends ExtraReducers = Record<string, never>> = AppStoreOf<R>['dispatch'];

export type AppThunk<ReturnType_ = void, R extends ExtraReducers = Record<string, never>> =
  ThunkAction<ReturnType_, RootStateOf<R>, unknown, UnknownAction>;
